import { isSmsCountryBlocked, SMS_BLOCKED_MESSAGE } from '../authSmsBlocked';
import { useEffect, useState } from 'react';
import {
  ForgetEmailLink,
  OpenInboxButton,
  getLastSignInMethod,
  getRememberedEmail,
  getRememberedPhone,
  rememberEmail,
  rememberPhone,
} from './auth/signInHelpers';
import { postSignInPath } from '../utils/postSignIn';
import { useLocation } from 'wouter';
import { auth } from '@mindstudio-ai/interface';
import api from '../api';
import { Button, DigitInput, BrandLogo } from '../components';
import { useAuth, useAuthModal } from '../state';
import { getStoredReferralCode } from '../referral';

/**
 * Sign in / Sign up popup.
 *
 * A lightweight modal that handles the email/phone -> 6-digit-code
 * VERIFICATION inline on the current page, so signing in (and the first
 * step of signing up) never bounces to a separate /signin or /signup page.
 *
 * Routing after verification mirrors the full AuthPage logic:
 *   - Fully-onboarded returning user -> refresh + close + go to /dashboard.
 *   - New / profile-incomplete account -> hand off to the full-page
 *     onboarding at /signup?onboard=1 (the profile form + welcome survey
 *     need more room than a popup), which resumes at the profile step
 *     because the session is already established here.
 *
 * The verification logic (send/verify SDK calls, transient-error retries,
 * error copy) is kept faithful to pages/Auth.tsx.
 */
type Step = 'email' | 'code';
type AuthMode = 'email' | 'phone';

function isTransientPlatformError(err: any): boolean {
  const msg = String(err?.message ?? '').toLowerCase();
  const status = err?.status ?? err?.statusCode;
  if (typeof status === 'number' && status >= 500) return true;
  return (
    msg.includes('fetch failed') ||
    msg.includes('bad gateway') ||
    msg.includes('502') ||
    msg.includes('503') ||
    msg.includes('504') ||
    msg.includes('network') ||
    msg.includes('owner_resolution_failed') ||
    msg.includes('timeout')
  );
}
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Races a promise against a timeout. Guards the rare failure mode where the
 * underlying HTTP request completes (e.g. the auth send endpoint returns 200)
 * but the SDK promise never settles — which otherwise leaves the submit button
 * spinning forever with no recovery. On timeout we reject with an error whose
 * message contains "timeout", so `isTransientPlatformError` classifies it as
 * transient and the surrounding retry loop retries it (and, if every attempt
 * stalls, surfaces the friendly "brief outage — try again" message with a
 * working button). Normal sends/verifies settle in well under a second, so a
 * 12s ceiling never trips on healthy calls.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timeout — the request did not complete in time.`));
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}
const AUTH_CALL_TIMEOUT_MS = 12000;

export function AuthModal() {
  const { open, mode: intent, close, next } = useAuthModal();
  const [, navigate] = useLocation();
  const refresh = useAuth((s) => s.refresh);

  const isSignup = intent === 'signup';

  const [step, setStep] = useState<Step>('email');
  // Default to phone on mobile/touch devices (where typing an email is more
  // friction and an SMS code is the natural flow); email elsewhere.
  const [mode, setMode] = useState<AuthMode>(() => {
    // Open on whichever method last signed in here, already filled in.
    const last = getLastSignInMethod();
    if (last) return last;
    try {
      const isMobile =
        window.matchMedia('(max-width: 540px)').matches ||
        window.matchMedia('(pointer: coarse)').matches;
      return isMobile ? 'phone' : 'email';
    } catch {
      return 'email';
    }
  });
  // Pre-filled with the last email that signed in on this device.
  const [email, setEmail] = useState(() => getRememberedEmail());
  // Pre-filled with the last phone that signed in on this device.
  const [phone, setPhone] = useState(() => getRememberedPhone()?.phone ?? '');
  const [countryCode, setCountryCode] = useState<string>(() => {
    const remembered = getRememberedPhone();
    if (remembered) return remembered.country;
    try {
      return auth.phone.detectCountry() ?? 'US';
    } catch {
      return 'US';
    }
  });
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Brief "Verified" confirmation shown while we route the user in.
  const [showSuccess, setShowSuccess] = useState(false);

  // Reset to a clean state whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setStep('email');
      setCode('');
      setError(null);
      setShowSuccess(false);
      setVerificationId('');
      setLoading(false);
    }
  }, [open]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, close]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = window.setInterval(
      () => setResendCooldown((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => window.clearInterval(t);
  }, [resendCooldown]);

  const normalizePhone = (): string | null => {
    const raw = phone.trim();
    if (!raw) return null;
    try {
      const e164 = auth.phone.toE164(raw, countryCode);
      return e164 && auth.phone.isValid(e164) ? e164 : null;
    } catch {
      return null;
    }
  };

  const handleSendCode = async () => {
    setError(null);
    setShowSuccess(false);
    let phoneE164: string | null = null;
    if (mode === 'email') {
      const e = email.trim();
      if (!e || !auth.email.isValid(e)) {
        setError('Enter a valid email address.');
        return;
      }
    } else {
      phoneE164 = normalizePhone();
      if (!phoneE164) {
        setError('Enter a valid phone number.');
        return;
      }
    }
    setLoading(true);
    try {
      let lastErr: any;
      let result: { verificationId: string } | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          result =
            mode === 'email'
              ? await withTimeout(auth.sendEmailCode(email.trim()), AUTH_CALL_TIMEOUT_MS, 'Send code')
              : await withTimeout(auth.sendSmsCode(phoneE164!), AUTH_CALL_TIMEOUT_MS, 'Send code');
          break;
        } catch (err: any) {
          lastErr = err;
          if (err?.code === 'rate_limited') throw err;
          if (!isTransientPlatformError(err)) throw err;
          await sleep(400 * Math.pow(2, attempt));
        }
      }
      if (!result) throw lastErr;
      setVerificationId(result.verificationId);
      setStep('code');
      setResendCooldown(30);
    } catch (err: any) {
      if (err?.code === 'rate_limited') {
        setError('Too many attempts. Try again later.');
      } else if (isTransientPlatformError(err)) {
        setError(
          'We could not reach the server. This is usually a brief outage. Wait a few seconds and try again.',
        );
      } else if (isSmsCountryBlocked(err)) {
        // Texts can't reach this country: move them to email (the field autofocuses).
        setMode('email');
        setError(SMS_BLOCKED_MESSAGE);
      } else {
        setError(err?.message ?? 'Could not send code. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (codeValue: string) => {
    setError(null);
    setLoading(true);
    try {
      let lastErr: any;
      let verified = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          if (mode === 'email') {
            await withTimeout(auth.verifyEmailCode(verificationId, codeValue), AUTH_CALL_TIMEOUT_MS, 'Verify');
            rememberEmail(email);
          } else {
            await withTimeout(auth.verifySmsCode(verificationId, codeValue), AUTH_CALL_TIMEOUT_MS, 'Verify');
            rememberPhone(countryCode, phone);
          }
          verified = true;
          break;
        } catch (err: any) {
          lastErr = err;
          if (
            err?.code === 'invalid_code' ||
            err?.code === 'verification_expired' ||
            err?.code === 'max_attempts_exceeded'
          ) {
            throw err;
          }
          if (!isTransientPlatformError(err)) throw err;
          await sleep(400 * Math.pow(2, attempt));
        }
      }
      if (!verified) throw lastErr;

      // Verified — flash the success confirmation while the routing decision
      // (onboarding-state fetch) runs, so the animation overlaps real work
      // rather than adding delay.
      setShowSuccess(true);

      // Decide where to route. Session now exists.
      let state: Awaited<ReturnType<typeof api.getOnboardingState>> | null =
        null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          state = await withTimeout(api.getOnboardingState(), AUTH_CALL_TIMEOUT_MS, 'Account state');
          break;
        } catch (err: any) {
          if (!isTransientPlatformError(err) || attempt === 2) throw err;
          await sleep(400 * Math.pow(2, attempt));
        }
      }
      if (!state) throw new Error('Could not load your account state.');

      // Fully-onboarded user (or legacy free-text-name user): sign in and
      // go to the post-auth target (a chosen plan, etc.) or the dashboard.
      // The popup closes; no separate auth page.
      if (state.profileComplete) {
        await refresh();
        close();
        navigate(postSignInPath(next));
        return;
      }

      // New / incomplete account: hand off to the full-page onboarding,
      // which resumes at the profile step (session already established).
      // Carry the verified email so the onboarding can still offer the
      // newsletter opt-in, and carry `next` so the chosen plan survives the
      // onboarding and lands the user on the subscription page afterward.
      close();
      const params = new URLSearchParams({ onboard: '1' });
      if (mode === 'email' && email.trim()) params.set('email', email.trim());
      if (next) params.set('next', next);
      navigate(`/signup?${params.toString()}`);
    } catch (err: any) {
      if (err?.code === 'invalid_code') setError('Wrong code. Try again.');
      else if (err?.code === 'verification_expired')
        setError('Code expired. Request a new one.');
      else if (err?.code === 'max_attempts_exceeded')
        setError('Too many attempts. Request a new code.');
      else if (isTransientPlatformError(err)) {
        setError(
          'We could not reach the server. This is usually a brief outage. Wait a few seconds and try again.',
        );
      } else setError(err?.message ?? 'Verification failed.');
      setShowSuccess(false);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 6 digits entered.
  useEffect(() => {
    if (open && step === 'code' && code.length === 6 && !loading) {
      void handleVerify(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step, open]);

  if (!open) return null;

  return (
    <div
      className="authmodal__scrim"
      role="dialog"
      aria-modal="true"
      aria-label={isSignup ? 'Sign up' : 'Sign in'}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) close();
      }}
    >
      <style>{AUTHMODAL_CSS}</style>
      <div className="authmodal__card">
        <button
          type="button"
          className="authmodal__close"
          aria-label="Close"
          onClick={() => !loading && close()}
        >
          &times;
        </button>

        <div className="authmodal__brand">
          <BrandLogo size={30} />
        </div>

        {step === 'email' && (
          <>
            <h2 className="authmodal__title f-display">
              {isSignup ? 'Create your account' : 'Welcome back'}
            </h2>
            <p className="authmodal__sub">
              {isSignup
                ? `Sign up with your ${mode === 'email' ? 'email' : 'phone'} to save your work and generate your invention strategy memo.`
                : `Enter your ${mode === 'email' ? 'email' : 'phone'} to sign in.`}
            </p>

            {/* Referral welcome — shown only when the visitor arrived through a
                friend's link and is signing up. */}
            {isSignup && getStoredReferralCode() && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  padding: '10px 12px',
                  marginBottom: 16,
                  borderRadius: 10,
                  background: 'rgba(31,157,99,0.08)',
                  border: '1px solid rgba(31,157,99,0.22)',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--ink)',
                }}
              >
                <span aria-hidden style={{ color: 'var(--spark-deep)', fontWeight: 700, flexShrink: 0 }}>✦</span>
                <span>A friend invited you. You&apos;ll <strong>both get a free strategy session</strong> when you subscribe.</span>
              </div>
            )}

            <div className="authmodal__toggle" role="tablist" aria-label="Sign-in method">
              {(['email', 'phone'] as AuthMode[]).map((m) => (
                <button
                  key={m}
                  role="tab"
                  aria-selected={mode === m}
                  type="button"
                  className="f-mono"
                  onClick={() => {
                    if (mode === m) return;
                    setMode(m);
                    setError(null);
                  }}
                  data-active={mode === m}
                >
                  {m === 'email' ? 'Email' : 'Phone'}
                </button>
              ))}
            </div>

            {mode === 'email' ? (
              <div className="form-row">
                <label className="field-label" htmlFor="authmodal-email">
                  Email
                </label>
                <input
                  id="authmodal-email"
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSendCode();
                  }}
                  placeholder="you@example.com"
                  autoFocus
                  autoComplete="email"
                  inputMode="email"
                />
                {email && email === getRememberedEmail() && <ForgetEmailLink onForget={() => setEmail('')} />}
              </div>
            ) : (
              <div className="form-row">
                <label className="field-label" htmlFor="authmodal-phone">
                  Phone number
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    aria-label="Country"
                    className="input"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    style={{ flex: '0 0 130px', cursor: 'pointer' }}
                  >
                    {auth.phone.countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.dialCode}
                      </option>
                    ))}
                  </select>
                  <input
                    id="authmodal-phone"
                    className="input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleSendCode();
                    }}
                    placeholder="(555) 123-4567"
                    autoFocus
                    autoComplete="tel"
                    inputMode="tel"
                    style={{ flex: 1 }}
                  />
                </div>
                <div className="field-help">
                  We will text you a 6-digit code. Standard message rates apply.
                </div>
                {phone && phone === getRememberedPhone()?.phone && (
                  <ForgetEmailLink kind="phone" onForget={() => setPhone('')} />
                )}
              </div>
            )}

            {error && (
              <div className="authmodal__error">
                {error}
                {error === SMS_BLOCKED_MESSAGE && mode !== 'email' && (
                  <div style={{ marginTop: 8 }}>
                    <button type="button" className="auth-switch-email" onClick={() => { setMode('email'); setError(null); }}>
                      Use email instead
                    </button>
                  </div>
                )}
              </div>
            )}

            <Button onClick={handleSendCode} loading={loading} block>
              {isSignup ? 'Send code & sign up' : 'Send code & sign in'}
            </Button>

            <div className="authmodal__switch">
              {isSignup ? (
                <>
                  Already have an account?{' '}
                  <button type="button" onClick={() => useAuthModal.getState().openModal('signin')}>
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  New here?{' '}
                  <button type="button" onClick={() => useAuthModal.getState().openModal('signup')}>
                    Create an account
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {step === 'code' && (
          <>
            <h2 className="authmodal__title f-display">Enter your code</h2>
            <p className="authmodal__sub">
              We sent a 6-digit code to{' '}
              <strong>{mode === 'email' ? email.trim() : normalizePhone()}</strong>.{' '}
              {mode === 'phone'
                ? 'On your phone it can fill in automatically, or paste it here.'
                : 'Paste it here, or type it in.'}
            </p>
            <DigitInput
              value={code}
              onChange={setCode}
              autoFocus
              disabled={loading}
              status={showSuccess ? 'success' : error ? 'error' : 'idle'}
            />
            {/* Fixed-height status slot so verifying/success never shift layout. */}
            <div className="authmodal__statusrow">
              {showSuccess ? (
                <span className="authmodal__status authmodal__status--success">
                  <span className="authmodal__check" aria-hidden>
                    ✓
                  </span>{' '}
                  Verified
                </span>
              ) : loading ? (
                <span className="authmodal__status authmodal__status--verify">
                  <span className="authmodal__spinner" aria-hidden /> Verifying…
                </span>
              ) : error ? (
                <span className="authmodal__error-text">{error}</span>
              ) : null}
            </div>
            <div className="authmodal__coderow">
              {resendCooldown > 0 ? (
                <span className="authmodal__cooldown">Resend in {resendCooldown}s</span>
              ) : (
                <button
                  type="button"
                  className="btn btn--tertiary"
                  onClick={() => {
                    setCode('');
                    void handleSendCode();
                  }}
                >
                  Resend code
                </button>
              )}
              <button
                type="button"
                className="btn btn--tertiary"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                }}
              >
                Use a different {mode === 'email' ? 'email' : 'phone'}
              </button>
            </div>
            {mode === 'email' && <OpenInboxButton email={email.trim()} />}
          </>
        )}
      </div>
    </div>
  );
}

const AUTHMODAL_CSS = `
.authmodal__scrim {
  position: fixed;
  inset: 0;
  z-index: 240;
  background: rgba(15, 23, 20, 0.55);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: authmodalFade 180ms ease;
}
@keyframes authmodalFade { from { opacity: 0; } to { opacity: 1; } }
.authmodal__card {
  position: relative;
  /* Lab warm treatment: ivory surface, warm hairline, warm-toned shadow —
     matching the reseated cards on /ve rather than a cold white panel. */
  background: #FDFBF5;
  border: 1px solid rgba(58, 42, 24, 0.14);
  border-radius: 18px;
  width: 100%;
  max-width: 440px;
  padding: 36px 36px 32px;
  box-shadow: 0 1px 2px rgba(58, 42, 24, 0.06), 0 24px 64px -20px rgba(58, 42, 24, 0.4);
  animation: authmodalIn 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
/* Brass focus accent on the modal's inputs — the Lab proof accent. */
.authmodal__card .input:focus {
  border-color: rgba(181, 133, 60, 0.6);
  box-shadow: 0 0 0 3px rgba(181, 133, 60, 0.12);
  outline: none;
}
@keyframes authmodalIn {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: none; }
}
.authmodal__close {
  position: absolute;
  top: 16px;
  right: 18px;
  background: none;
  border: none;
  font-size: 26px;
  line-height: 1;
  color: var(--text-decorative);
  cursor: pointer;
  padding: 4px;
}
.authmodal__close:hover { color: var(--ink); }
.authmodal__brand { margin-bottom: 20px; }
.authmodal__title {
  font-size: 27px;
  font-weight: 500;
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0 0 8px;
}
.authmodal__sub {
  font-size: 14.5px;
  line-height: 1.5;
  color: var(--text-decorative);
  margin: 0 0 22px;
}
.authmodal__toggle {
  display: inline-flex;
  margin-bottom: 22px;
  border: 1px solid var(--border-hairline);
  border-radius: 999px;
  padding: 3px;
  background: var(--surface-card);
}
.authmodal__toggle button {
  border: none;
  cursor: pointer;
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  color: var(--text-decorative);
  transition: background 120ms ease, color 120ms ease;
}
.authmodal__toggle button[data-active='true'] {
  background: var(--ink);
  color: var(--parchment);
}
.authmodal__error {
  color: var(--oxblood);
  font-size: 14px;
  margin-bottom: 16px;
}
/* Fixed-height feedback slot under the code boxes — verifying / success /
   error all live here so none of them shift the layout. */
.authmodal__statusrow {
  min-height: 22px;
  margin-top: 14px;
  display: flex;
  align-items: center;
}
.authmodal__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}
.authmodal__status--verify { color: var(--text-decorative); }
.authmodal__status--success {
  color: #1f9d63;
  font-weight: 600;
  animation: authmodalStatusIn 200ms ease;
}
.authmodal__error-text { color: var(--oxblood); font-size: 14px; }
.authmodal__spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(58, 42, 24, 0.2);
  border-top-color: var(--ink);
  animation: authmodalSpin 700ms linear infinite;
  flex-shrink: 0;
}
.authmodal__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #1f9d63;
  color: #fff;
  font-size: 11px;
  line-height: 1;
  flex-shrink: 0;
  animation: authmodalPop 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes authmodalSpin { to { transform: rotate(360deg); } }
@keyframes authmodalPop {
  from { transform: scale(0.4); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
@keyframes authmodalStatusIn {
  from { opacity: 0; transform: translateY(2px); }
  to { opacity: 1; transform: none; }
}
.authmodal__switch {
  margin-top: 22px;
  font-size: 14px;
  color: var(--text-decorative);
}
.authmodal__switch button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  color: var(--ink);
  font-size: 14px;
  font-family: inherit;
}
.authmodal__coderow {
  margin-top: 20px;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  min-height: 28px;
}
.authmodal__cooldown { font-size: 13px; color: var(--text-decorative); }

@media (max-width: 540px) {
  /* Bottom sheet on mobile so it stays thumb-friendly. */
  .authmodal__scrim { align-items: flex-end; padding: 0; }
  .authmodal__card {
    max-width: none;
    border-radius: 20px 20px 0 0;
    padding: 28px 22px calc(28px + env(safe-area-inset-bottom, 0px));
    animation: authmodalSheetIn 260ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes authmodalSheetIn {
    from { transform: translateY(100%); }
    to { transform: none; }
  }
}
`;
